import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { REVIEWS } from "../data/tour";
import { Reveal } from "./Reveal";

export const Testimonial = () => (
  <section id="reviews" data-testid="testimonial-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-3">
        <div className="flex items-baseline gap-4">
          <span className="font-sans text-xs tracking-[0.3em] text-marigold">03</span>
          <p className="text-xs uppercase tracking-[0.3em] text-sage">Traveller Stories</p>
        </div>
        <Quote size={56} className="mt-8 text-marigold/30" strokeWidth={1} />
      </Reveal>

      <Reveal delay={0.12} className="lg:col-span-9">
        <blockquote data-testid="testimonial-quote" className="max-w-3xl font-serif text-2xl font-medium leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
          “Had an incredible backpacking adventure with TrekPanda in Kerala. Our captain,
          <span className="italic text-marigold"> Prathamesh</span>, led us expertly through the
          stunning landscapes of Munnar, Thekkady, Alleppey, and Varkala — an elephant ride, jungle
          safari, serene shikara and boat rides through the mangroves, and the breathtaking tea and
          flower gardens of Munnar.”
        </blockquote>
        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-vine pt-8">
          <div className="flex items-center gap-1.5" data-testid="testimonial-rating">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="fill-marigold text-marigold" />
            ))}
          </div>
          <div>
            <p className="text-sm font-semibold text-cream">A TrekPanda Traveller</p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-sage">
              Kerala with Kanyakumari · 27 Sept – 5 Oct batch
            </p>
          </div>
          <p className="text-sm italic text-sage lg:ml-auto">
            “A memorable journey with a great group of friends. Highly recommend!”
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15} className="lg:col-span-12">
        <div className="mt-14 grid grid-cols-1 gap-px border border-vine bg-vine sm:grid-cols-2 lg:grid-cols-4">
          {REVIEWS.map((r, i) => (
            <article key={r.name} data-testid={`review-card-${i}`} className="flex flex-col bg-moss p-7 transition-colors duration-500 hover:bg-jungle">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={13} className="fill-marigold text-marigold" />
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-sage">“{r.text}”</p>
              <div className="mt-6 border-t border-vine pt-4">
                <p className="text-sm font-semibold text-cream">{r.name}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-sage/70">{r.batch}</p>
              </div>
            </article>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-14 lg:col-span-12">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <motion.figure
            className="group overflow-hidden rounded-xl"
            initial={{ clipPath: "inset(12% 12% 12% 12%)", opacity: 0 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src="/group-varkala-sunset.jpg"
              alt="TrekPanda Kerala batch group photo at Varkala beach sunset"
              data-testid="gallery-varkala-photo"
              className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] text-sage">
              The whole batch at Varkala beach — sunset hour
            </figcaption>
          </motion.figure>
          <motion.figure
            className="group overflow-hidden rounded-xl"
            initial={{ clipPath: "inset(12% 12% 12% 12%)", opacity: 0 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src="/group-cultural-show.jpg"
              alt="TrekPanda batch with Kalaripayattu artists in Thekkady"
              data-testid="gallery-cultural-photo"
              className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] text-sage">
              Kalaripayattu night with the artists — Thekkady
            </figcaption>
          </motion.figure>
          <motion.figure
            className="group overflow-hidden rounded-xl"
            initial={{ clipPath: "inset(12% 12% 12% 12%)", opacity: 0 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src="/group-ripple-tea.jpg"
              alt="TrekPanda batch selfie at Ripple Tea garden, Munnar"
              data-testid="gallery-ripple-tea-photo"
              className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] text-sage">
              Captain's selfie at Ripple Tea — Munnar
            </figcaption>
          </motion.figure>
        </div>
      </Reveal>
    </div>
  </section>
);
